// ----------------------------------------------------------------------
// <copyright file="Izenda.AccountInfo.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

!(function() {
  const { isNil, map, forEach, isEqual, min, differenceBy, take, find } = require('lodash/fp');

  var m = require('mobx');
  var SecurityQuestion = require('logic/models/Izenda.SecurityQuestion');
  var AccountConstants = require('logic/data/Izenda.AccountConstants');
  var trackable = require('share/tracking/Izenda.Trackable');
  var SecurityQuestionReadOnly = require('logic/models/Izenda.SecurityQuestionReadOnly');

  function AccountInfo(mode, data, systemConfig) {
    // NOTE: intended for activation.
    this.numberOfQuestionProfile = null;
    this.fullSecurityQuestions = null; // array of SecurityQuestionReadOnly
    this.mode = mode || AccountConstants.MODES.PASSWORD_SETUP;

    var props = {
      // DTO
      id: null,
      systemAdmin: false,
      userName: null,
      firstName: null,
      lastName: null,
      emailAddress: null,
      verification: null,
      userSecurityQuestions: [], // Array of SecurityQuestion
      password: null,
      tenantDisplayID: null,
      tenantName: null,
      roles: [],
      dataOffset: null,
      timestampOffset: null,
      cultureName: 'en-US',
      dateFormat: 'MM/DD/YYYY',
      tenantId: null,
      fullName: null,
      passwordLastChanged: null,
      securityQuestionLastChanged: null,

      // For UI logic
      isExpiration: null, // null: not check yet, false: expired, true: not expiration.
      confirmPassword: null,
      message: null,
      currentStep: null,
      hasOrg: false,
      enableForEnter: false,
      hasChangeLanguage: false,
      // Computed
      securityQuestions: function() {
        return differenceBy('securityQuestionId', this.fullSecurityQuestions, this.userSecurityQuestions);
      }.bind(this),

      isSystemUser: function() {
        return isNil(this.tenantId);
      },

      languageList: [],
      dateFormatList: [],
      alertMessage: {},
      selectedQuestionList: [],
      currentModules: []
    };

    m.extendObservable(trackable(this), props);
    systemConfig && this.populateSystemConfig(systemConfig);
    data && this.populate(data);
  }

  /**
   * Populate system (system/tenant) config info.
   * @param  {Object} systemConfig
   */
  AccountInfo.prototype.populateSystemConfig = function(systemConfig) {
    this.numberOfQuestionProfile = systemConfig.numberOfQuestionProfile;
    this.fullSecurityQuestions = systemConfig.fullSecurityQuestions;
    return Promise.resolve(true);
  };

  /**
   * Populate date.
   * @param  {Object} data
   */
  AccountInfo.prototype.populate = m.action(function(data) {
    this.tenantDisplayID = data.tenantDisplayId;
    this.tenantName = data.tenantName;
    this.id = data.id;
    this.systemAdmin = data.systemAdmin;
    this.userName = data.userName;
    this.firstName = data.firstName;
    this.lastName = data.lastName;
    this.fullName = data.fullName;
    this.emailAddress = data.emailAddress;
    this.verification = data.verification;
    this.tenantId = data.tenantId;
    this.roles = data.roles;
    this.dataOffset = data.dataOffset;
    this.timestampOffset = data.timestampOffset;
    this.cultureName = data.cultureName;
    this.dateFormat = data.dateFormat;
    this.passwordLastChanged = data.passwordLastChanged;
    this.securityQuestionLastChanged = data.securityQuestionLastChanged;
    this.hasChangeLanguage = false;
    this.currentModules = data.currentModules;

    this.populateUserSecurityQuestions(data.userSecurityQuestions);
    this.getSelectedQuestionList();
  });

  /**
   * Update current step.
   * @param  {String} value
   */
  AccountInfo.prototype.updateCurrentStep = m.action(function(value) {
    this.currentStep = value;
    this.message = null;

    switch (value) {
      case AccountConstants.STEP_KEYS.SecurityInfo:
        this.resetPasswordInfo();
        this.resetUserSecurityQuestions();
        break;
      case AccountConstants.STEP_KEYS.QuestionInfo:
        this.resetUserSecurityQuestions();
        break;
      case AccountConstants.STEP_KEYS.PasswordInfo:
        this.resetPasswordInfo();
        break;
    }
  });

  /**
   * Reset security info.
   */
  AccountInfo.prototype.resetSecurityInfo = m.action(function() {
    this.password = null;
    this.confirmPassword = null;
  });

  /**
   * Update property's value.
   */
  AccountInfo.prototype.updateProperty = m.action(function(key, value) {
    this[key] = value;
    this.markDirty();

    // mark change language
    if (key === 'cultureName') {
      this.hasChangeLanguage = true;
    }
  });

  /**
   * Reset security info.
   */
  AccountInfo.prototype.resetUserSecurityQuestions = m.action(function() {
    forEach(function(item) {
      item.answer = null;
    }, this.userSecurityQuestions);
  });

  /**
   * Reset password info.
   */
  AccountInfo.prototype.resetPasswordInfo = m.action(function() {
    this.password = null;
    this.confirmPassword = null;
  });

  /**
   * Check whether given step is the current step.
   * @param  {String} value
   */
  AccountInfo.prototype.isCurrentStep = function(value) {
    return isEqual(value, this.currentStep);
  };

  /**
   * Check mode.
   * @param  {String} value
   */
  AccountInfo.prototype.isMode = function(value) {
    return isEqual(value, this.mode);
  };

  /**
   * Build default security questions.
   * It based con config and number of questions.
   */
  AccountInfo.prototype.populateUserSecurityQuestions = m.action(function(defaultQuestions) {
    this.userSecurityQuestions && this.userSecurityQuestions.clear();
    if (isNil(defaultQuestions) && !isNil(this.fullSecurityQuestions)) {
      var count = min([this.numberOfQuestionProfile, this.fullSecurityQuestions.length]);
      defaultQuestions = take(count, this.fullSecurityQuestions);
    }

    forEach(
      function(item) {
        this.userSecurityQuestions.push(new SecurityQuestion(item, this.fullSecurityQuestions));
      }.bind(this),
      defaultQuestions
    );

    return Promise.resolve(true);
  });

  /**
   * Get selected question list
   */
  AccountInfo.prototype.getSelectedQuestionList = m.action(function() {
    this.selectedQuestionList = map(
      function(q) {
        var data = {
          securityQuestionId: q.securityQuestionId
        };
        return new SecurityQuestion(data, this.fullSecurityQuestions);
      }.bind(this),
      this.userSecurityQuestions
    );

    var count = this.numberOfQuestionProfile - this.userSecurityQuestions.length;
    if (count > 0) {
      var remainingQuestionList = differenceBy(
        'securityQuestionId',
        this.fullSecurityQuestions,
        this.userSecurityQuestions
      );
      remainingQuestionList = map(
        function(q) {
          var data = {
            securityQuestionId: q.securityQuestionId
          };
          return new SecurityQuestion(data, this.fullSecurityQuestions);
        }.bind(this),
        take(count, remainingQuestionList)
      );
      forEach(
        function(q) {
          this.selectedQuestionList.push(q);
        }.bind(this),
        remainingQuestionList
      );
    }
  });

  /**
   * Update security question (include answer)
   * @param  {Object} question
   * @param  {String} property
   * @param  {String} value
   */
  AccountInfo.prototype.updateUserSecurityQuestion = m.action(function(question, property, value) {
    var matchedQuestion = find(function(item) {
      return isEqual(item.securityQuestionId, question.securityQuestionId);
    }, this.userSecurityQuestions);

    if (!isNil(matchedQuestion)) {
      matchedQuestion[property] = value;
    }
  });

  /**
   * Build security questions for saving
   */
  AccountInfo.prototype.buildUserSecurityQuestionsForSaving = function() {
    return map(
      function(item) {
        return {
          userId: this.id,
          securityQuestionId: item.securityQuestionId,
          answer: item.answer
        };
      }.bind(this),
      this.userSecurityQuestions
    );
  };

  /**
   * Populate my preference information
   * @param {Array} language list
   * @param {Array} date format list
   * @param {Array} role list
   */
  AccountInfo.prototype.populateMyProfile = m.action(function getMyPreferenceInfo(
    currentUser,
    languageList,
    dateFormatList,
    questions,
    config
  ) {
    this.numberOfQuestionProfile = config.numberOfQuestionProfile;
    this.fullSecurityQuestions = map(function(item) {
      return new SecurityQuestionReadOnly(item);
    }, questions);
    this.populate(currentUser.data);
    this.languageList = languageList;
    this.dateFormatList = map(function(item) {
      return {
        labelDateFormat: item,
        dateFormat: item
      };
    }, dateFormatList);
  });

  /**
   * Build data for saving
   * @returns {Object} data
   */
  AccountInfo.prototype.buildDataForSaving = function() {
    return {
      id: this.id,
      systemAdmin: this.systemAdmin,
      userName: this.userName,
      firstName: this.firstName,
      lastName: this.lastName,
      cultureName: this.cultureName,
      dateFormat: this.dateFormat,
      tenantId: this.tenantId,
      emailAddress: this.emailAddress,
      roles: this.roles,
      dataOffset: this.dataOffset,
      timestampOffset: this.timestampOffset,
      tenantName: this.tenantName,
      hasChangeLanguage: this.hasChangeLanguage
    };
  };

  /**
   * Reset
   * @param {Object} current user
   */
  AccountInfo.prototype.reset = m.action(function(currentUser) {
    if (!isNil(currentUser)) {
      this.userName = currentUser.userName;
      this.firstName = currentUser.firstName;
      this.lastName = currentUser.lastName;
      this.cultureName = currentUser.cultureName;
      this.dateFormat = currentUser.dateFormat;
      this.emailAddress = currentUser.emailAddress;
      this.dataOffset = currentUser.dataOffset;
      this.timestampOffset = currentUser.timestampOffset;
    }
    this.alertMessage = {};
  });

  /**
   * Get remaining question list
   * @param {Object} question
   * @returns {Object} remaining question list
   */
  AccountInfo.prototype.getRemainingQuestionList = function(question) {
    var remainingQuestions = [question];
    var remainingQuestionList = differenceBy(
      'securityQuestionId',
      this.fullSecurityQuestions,
      this.selectedQuestionList
    );

    forEach(
      function(q) {
        var data = {
          securityQuestionId: q.securityQuestionId
        };
        remainingQuestions.push(new SecurityQuestion(data, this.fullSecurityQuestions));
      }.bind(this),
      remainingQuestionList
    );

    return remainingQuestions;
  };

  /**
   * Build for update security questions
   * @returns {Object} security questions
   */
  AccountInfo.prototype.buildForUpdateSecurityQuestions = function() {
    return {
      userSecurityQuestions: map(function(q) {
        return {
          securityQuestionId: q.securityQuestionId,
          answer: q.answer
        };
      }, this.selectedQuestionList),
      userName: this.userName,
      id: this.id
    };
  };

  /**
   * Reset answers
   */
  AccountInfo.prototype.resetAnswers = m.action(function() {
    forEach(function(q) {
      q.answer = null;
    }, this.selectedQuestionList);
  });

  /**
   * Reset validation
   */
  AccountInfo.prototype.resetValidation = function() {
    this.alertMessage = {};
  };

  /**
   * Reset for login
   */
  AccountInfo.prototype.resetForLogin = m.action(function() {
    this.password = null;
    this.isExpiration = null;
    this.confirmPassword = null;
    this.message = null;
    this.currentStep = null;
    this.hasOrg = false;
    this.enableForEnter = false;
    this.hasChangeLanguage = false;
    this.populate({});
  });

  module.exports = AccountInfo;
})();



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.AccountInfo.js